import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useNavigate } from 'react-router-dom';

// Custom SVG Icons
const Heart = ({ size = 24, className = "", filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const MapPin = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const Users = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const ExternalLink = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
    <polyline points="15,3 21,3 21,9"></polyline>
    <line x1="10" y1="14" x2="21" y2="3"></line>
  </svg>
);

function YourFavoriteResidencies() {
  const [favorites, setFavorites] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!auth.currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const favoritesRef = doc(db, "favorites", auth.currentUser.uid);
        const favoritesSnap = await getDoc(favoritesRef);

        if (favoritesSnap.exists()) {
          const data = favoritesSnap.data();
          // Remove metadata fields and only keep specialty data
          const { createdAt, updatedAt, ...specialtyData } = data;
          setFavorites(specialtyData);
        }
      } catch (error) {
        console.error("Error fetching favorites:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isLoading]);

  const viewProgram = (specialty, program) => {
    navigate(`/compare/${specialty}`, {
      state: { collegeData: program, specialty: specialty }
    });
  };

  const getTotalFavorites = () => {
    return Object.values(favorites).reduce((total, specialtyFavorites) => {
      return total + (specialtyFavorites?.length || 0);
    }, 0);
  };

  const formatType = (type) => {
    if (!type) return 'N/A';
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[15px] text-gray-500 dark:text-gray-400">Loading your favorites...</p>
        </div>
      </div>
    );
  }

  const totalFavorites = getTotalFavorites();

  if (totalFavorites === 0) {
    return (
      <div
        className="text-center py-12"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        <div className="w-20 h-20 bg-black/[0.04] dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Heart size={40} className="text-gray-400 dark:text-gray-500" filled />
        </div>
        <h3 className="text-[20px] font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">No Favorites Yet</h3>
        <p className="text-[15px] text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
          Start exploring programs and add them to your favorites for quick access.
        </p>
        <button
          onClick={() => navigate('/my-lists')}
          className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[15px] font-medium transition-all duration-200 active:scale-[0.96]"
        >
          Explore Programs
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="flex flex-wrap items-start justify-between gap-4"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        <div>
          <h2 className="text-[20px] font-semibold text-black dark:text-white mb-1 tracking-[-0.02em] flex items-center gap-2">
            <Heart className="text-gray-500 dark:text-gray-400" size={22} filled />
            Your Favorite Residencies
          </h2>
          <p className="text-[14px] text-gray-500 dark:text-gray-400">Programs you've saved for quick access and comparison</p>
        </div>
        <div className="bg-gradient-to-r from-blue-50/70 to-blue-100/40 dark:from-gray-700 dark:to-gray-800 rounded-2xl px-5 py-4 border border-black/8 dark:border-gray-600 border-l-2 border-l-blue-400/40 dark:border-l-blue-400/50">
          <div className="text-2xl font-semibold text-blue-600/90 dark:text-blue-400">{totalFavorites}</div>
          <div className="text-[12px] text-gray-600 dark:text-gray-400">Saved Programs</div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { value: Object.keys(favorites).length, label: 'Specialties' },
          { value: totalFavorites, label: 'Total Programs' },
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-6 border border-black/8 dark:border-gray-700"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.08 + i * 0.06}s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.08 + i * 0.06}s`
            }}
          >
            <div className="text-center">
              <div className="text-3xl font-semibold text-black dark:text-white mb-1">{stat.value}</div>
              <div className="text-[13px] text-gray-500 dark:text-gray-400 font-medium">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Favorites by Specialty */}
      <div className="space-y-6">
        {Object.entries(favorites).map(([specialty, programs], sIdx) => (
          programs && programs.length > 0 && (
            <div
              key={specialty}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 overflow-hidden"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(10px)',
                transition: `opacity 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.18 + sIdx * 0.09}s, transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.18 + sIdx * 0.09}s`
              }}
            >
              {/* Specialty Header */}
              <div className="bg-black/[0.02] dark:bg-white/5 px-6 py-4 border-b border-black/6 dark:border-gray-600">
                <h3 className="text-[17px] font-semibold text-black dark:text-white tracking-[-0.01em]">{specialty}</h3>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">{programs.length} saved program{programs.length !== 1 ? 's' : ''}</p>
              </div>

              {/* Programs Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {programs.map((program, index) => (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-700/80 rounded-xl border border-black/8 dark:border-gray-600 p-5 hover:bg-black/[0.01] dark:hover:bg-gray-600/80 hover:shadow-sm"
                      style={{
                        opacity: mounted ? 1 : 0,
                        transform: mounted ? 'translateY(0)' : 'translateY(8px)',
                        transition: `opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.26 + sIdx * 0.09 + index * 0.06}s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.26 + sIdx * 0.09 + index * 0.06}s, box-shadow 0.2s ease`
                      }}
                    >
                      <div className="mb-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-[15px] font-semibold text-black dark:text-white line-clamp-2 flex-1">
                            {program['Program Name'] || program['PROGRAM NAME'] || 'Program Name Not Available'}
                          </h4>
                          <span className="text-[11px] bg-black/5 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full font-medium whitespace-nowrap border border-black/10 dark:border-gray-600">
                            {specialty}
                          </span>
                        </div>
                        <div className="flex items-center text-[13px] text-gray-500 dark:text-gray-400 mb-1">
                          <MapPin size={14} className="mr-1.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          <span>
                            {(program.City || program.CITY) && (program.State || program.STATE)
                              ? `${program.City || program.CITY}, ${program.State || program.STATE}`
                              : (program.State || program.STATE) || (program.City || program.CITY) || 'Location Not Available'}
                          </span>
                        </div>
                        {(program.Type || program.TYPE) && (
                          <div className="text-[13px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span> {formatType(program.Type || program.TYPE)}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5 mb-4">
                        {program.POSITIONS && (
                          <div className="flex items-center text-[13px] text-gray-500 dark:text-gray-400">
                            <Users size={14} className="mr-2 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <span><span className="font-medium text-gray-700 dark:text-gray-300">Positions:</span> {program.POSITIONS}</span>
                          </div>
                        )}
                        {(program['%NON US IMGs'] || program['% NON US IMGS']) && (
                          <div className="text-[13px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Non-US IMGs:</span> {program['%NON US IMGs'] || program['% NON US IMGS']}%
                          </div>
                        )}
                        {(program['INV STEP 2'] || program['STEP 2 CUTOFF']) && (
                          <div className="text-[13px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Step 2 Cutoff:</span> {program['INV STEP 2'] || program['STEP 2 CUTOFF']}
                          </div>
                        )}
                        {(program.USCE || program['USCE']) && (
                          <div className="text-[13px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-300">USCE:</span> {program.USCE || program['USCE']}
                          </div>
                        )}
                        {(program.Visa || program.VISA) && (
                          <div className="text-[13px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Visa Support:</span> {program.Visa || program.VISA}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => viewProgram(specialty, program)}
                          className="flex-1 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-4 py-2.5 rounded-full text-[13px] font-medium transition-all duration-200 active:scale-[0.96]"
                        >
                          Compare Program
                        </button>
                        {(program.WEBSITE || program['RESIDENTS/ PROGRAM WEBSITE']) && (
                          <button
                            onClick={() => window.open(program.WEBSITE || program['RESIDENTS/ PROGRAM WEBSITE'], '_blank')}
                            className="border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 px-3 py-2.5 rounded-full transition-all duration-200 active:scale-[0.9] flex items-center justify-center bg-white dark:bg-gray-700"
                            title="Visit Program Website"
                          >
                            <ExternalLink size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Action Section */}
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 p-8 text-center"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.42s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.42s'
        }}
      >
        <div className="mb-6">
          <div className="w-14 h-14 bg-black/5 dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Heart size={28} className="text-gray-500 dark:text-gray-400" filled />
          </div>
          <h3 className="text-[18px] font-semibold text-black dark:text-white mb-2 tracking-[-0.01em]">Keep Exploring</h3>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
            Discover more programs that match your criteria and add them to your favorites.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/my-lists')}
            className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[14px] font-medium transition-all duration-200 active:scale-[0.96]"
          >
            Browse All Specialties
          </button>
        </div>
      </div>
    </div>
  );
}

export default YourFavoriteResidencies;